/**
 * The editor's live lint (#169 slice 3): the backend's preview of the
 * document, `PREVIEW_DEBOUNCE_MS` after the typing stops, and at once on
 * open. Preview reads the default branch's other definitions as the
 * viewer, so it never runs on a timer or on window focus.
 *
 * `result` is the preview of exactly this name and text, or undefined
 * while that one is still pending; `shown` keeps the last one landed, for
 * the findings to stay put while the next check runs.
 */

import { keepPreviousData, useQuery, type QueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { previewDefinition } from '../api/projectPanel';
import { projectPanelKeys } from './useProjectPanel';
import type { PreviewResponse } from '../types/projectPanel.types';

export const PREVIEW_DEBOUNCE_MS = 500;

export interface DefinitionPreview {
  result: PreviewResponse | undefined;
  shown: PreviewResponse | undefined;
  checking: boolean;
  /** The current text's preview failed (an outage, a 403). */
  error: Error | null;
  retry: () => void;
}

interface Checked {
  name: string | null;
  text: string;
}

export function useDefinitionPreview(projectId: string, name: string | null, text: string): DefinitionPreview {
  const [settled, setSettled] = useState<Checked>({ name, text });
  useEffect(() => {
    const timer = setTimeout(() => {
      setSettled({ name, text });
    }, PREVIEW_DEBOUNCE_MS);
    return () => { clearTimeout(timer); };
  }, [name, text]);

  const query = useQuery({
    queryKey: projectPanelKeys.preview(projectId, settled.name, settled.text),
    queryFn: () => previewDefinition(projectId, settled.name ?? '', settled.text),
    enabled: projectId.length > 0 && settled.name !== null,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const current = name !== null && settled.name === name && settled.text === text && !query.isPlaceholderData;
  return {
    result: current ? query.data : undefined,
    shown: name === null ? undefined : query.data,
    checking: name !== null && (!current || query.isFetching),
    error: current && !query.isFetching ? query.error : null,
    retry: () => { void query.refetch(); },
  };
}

/** The preview now, for the switch to the form; it fills the same cache. */
export function fetchPreview(
  client: QueryClient,
  projectId: string,
  name: string,
  text: string,
): Promise<PreviewResponse> {
  return client.fetchQuery({
    queryKey: projectPanelKeys.preview(projectId, name, text),
    queryFn: () => previewDefinition(projectId, name, text),
    retry: false,
  });
}
